/* eslint-disable camelcase */
import * as React from "react"
import * as yup from "yup"
import moment from "moment"
import numeral from "numeral"
import { Appearance, TextInput, View, ViewStyle } from "react-native"
import { Cycle, Farm } from "../../../database"
import { DateTimePickerField } from "../../field/date-time-picker-field/date-time-picker-field"
import { FormProps } from "../form"
import { Field, Formik } from "formik"
import { ModelField } from "../../field/model-field/model-field"
import { NumeralField } from "../../field/numeral-field/numeral-field"
import { Text } from "../../text/text"
import { TextField } from "../../field/text-field/text-field"
import { color } from "../../../theme"
import { observer } from "mobx-react-lite"
import { translate } from "../../../i18n"
import { useCallback, useMemo, useState } from "react"
import { MultipleImageField } from "../../field/multiple-image-field/multiple-image-field"
import { WalkthroughElement } from "../../walkthrough-element/walkthrough-element"
import { FormControl } from "../../form-control/form-control"
import { Radio } from "native-base"

const TITLE: ViewStyle = {
  borderTopWidth: 1,
  borderColor: color.palette.background,
  paddingHorizontal: 16,
  paddingTop: 16,
  paddingBottom: 10
}

const BORDER: ViewStyle = {
  backgroundColor: color.palette.background,
  height: 4
}

const FORM_CONTROL: ViewStyle = {
  paddingHorizontal: 16,
  // marginBottom: 8,
  paddingBottom: 8,
  backgroundColor: color.palette.white
}

export interface MortalityFormProps extends FormProps {
  /**
   * An optional style override useful for padding & margin.
   */
  initialValues?: {
    farm_id?: number
    cycle_id?: number
    recorded_at?: string
    quantity?: number
    average_weight?: number
    multiplier?: number
    remark?: string
    files?: Array<any>,
  }
}

/**
 * Describe your component here
 */
export const MortalityForm = observer(function MortalityForm(props: MortalityFormProps) {
  const {
    initialValues: initialValuesProps = {},
    onSubmit: onSubmitProps,
    colorScheme = Appearance.getColorScheme(),
    innerRef,
    parentScrollViewRef: scrollViewRef,
   ...rest
  } = props

  const [basedOn, setBasedOn] = useState<"quantity"|"size">("quantity")

  const input = {
    total_weight: React.useRef<TextInput>(),
    size: React.useRef<TextInput>()
  }

  const nextInput = {
    total_weight: () => input.size.current && input.size.current.focus()
  }

  const onChangeBasedOn = useCallback((value: any) => {
    setBasedOn(value)
  }, [])

  const onSubmit = useCallback((values) => {
    onSubmitProps({
      ...values,
      quantity: values.quantity,
      files: values.files.map((f, i) => {
        const initialValueFile = (initialValuesProps.files || [])[i]
        const previousUri = ((initialValuesProps.files || [])[i] || {}).path || null
        const uri = (f || {}).uri || null
        if (uri !== previousUri || (!uri && initialValueFile)) {
          if (uri)
            return {
              ...initialValueFile,
              fileable_type: 'App\\Mortality',
              title: f.fileName,
              type: f.type,
              size: f.fileSize,
              path: uri
            }
          else if (!uri && initialValueFile?.id) // Delete
            return {
              _action: 'delete',
              ...initialValueFile,
            }
        }
        return initialValueFile
      }),
    })
  }, [initialValuesProps.files, onSubmitProps])

  const validationSchema = yup.object({
    farm_id: yup.number().nullable().required().label(translate("mortalityForm.farmId")),
    cycle_id: yup.number().nullable().required().label(translate("mortalityForm.cycleId")),
    recorded_at: yup.string().nullable().required().label(translate("mortalityForm.recordedAt")),
    quantity: yup.number().min(1).nullable().required().label(translate("mortalityForm.quantity")),
    average_weight: yup.number().nullable().required().label(translate("mortalityForm.averageWeight")),
    total_weight: yup.number().moreThan(0).max(1000).nullable().required().label(translate("mortalityForm.totalWeight")),
    size: yup.number().moreThan(0).max(1000).nullable().required().label(translate("mortalityForm.size")),
    multiplier: yup.number().moreThan(0).max(1000).label(translate("mortalityForm.multiplier")),
    files: yup.array().label(translate("mortalityForm.files")),
    remark: yup.string().ascii().max(255).nullable().label(translate("mortalityForm.remark")),
  })

  const initialValues = useMemo<MortalityFormProps["initialValues"]>(() => ({
    farm_id: null,
    cycle_id: null,
    ...initialValuesProps,
    files: [null, null, null].map((f, i) => (
      (initialValuesProps.files || [])[i] || {}).path_source || f
    ),
    quantity: initialValuesProps.quantity || null,
    average_weight: initialValuesProps.average_weight || null,
    recorded_at: initialValuesProps.recorded_at || moment().format('YYYY-MM-DD'),
    get total_weight() {
      if (this.quantity != null && this.average_weight != null)
        return this.quantity * this.average_weight / 1000;
      return null;
    },
    get size() {
      if (this.average_weight != null)
        return 1000 / this.average_weight
      return null;
    }
  }), [initialValuesProps]);

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      innerRef={innerRef}
      {...rest}
    >
      {({ initialValues, values, setFieldValue, setFieldTouched }) => (
        <>
          <ModelField
            name="farm_id"
            txPrefix="mortalityForm"
            colorScheme={colorScheme}
            formControlProps={{ isRequired: true }}
            modelPickerProps={{
              Model: Farm,
              isDisabled: !!initialValues.farm_id,
              onValueChange: async (value) => {
                await setFieldValue("cycle_id", null)
                await setFieldValue("farm_id", value || null)
                await setFieldTouched("farm_id")
              },
            }}
          />
          <View style={TITLE}>
            <Text tx="mortalityForm.mortality" preset="title3" colorScheme={colorScheme} />
          </View>
          <View style={BORDER} />
          <WalkthroughElement
            id="mortality-form"
            scrollViewRef={scrollViewRef}
            scrollOffset={250}
          >
            <ModelField
              key={`${values.farm_id}-cycle_id`}
              name="cycle_id"
              txPrefix="mortalityForm"
              colorScheme={colorScheme}
              formControlProps={{ isRequired: true }}
              modelPickerProps={{
                Model: Cycle,
                dataLabel: "pond_name",
                fetchData: (model, search) => (
                  model.fetch({
                    farm_id__eq: values.farm_id,
                    sort: 'started_at,desc|pond_name_alpha_numeric'
                  }).then((models) => models.filter((m) => (
                    `${m.pond_name} - ${moment(m.started_at).format("DD MMM YYYY")}${m.finished_at ? ` (${translate("Finished")})` : ""}`
                  ).toLowerCase().includes((search || '').toLowerCase())))
                ),
                renderSelectedItemText: (item) => item && `${item.pond_name} - ${moment(item.started_at).format("DD MMM YYYY")}${item.finished_at ? ` (${translate("Finished")})` : ""}`,
                clearable: true,
                isDisabled: !values.farm_id || !!initialValues.cycle_id,
                titleTx: "mortalityForm.cycleIdPlaceholder"
              }}
            />
            <DateTimePickerField
              name="recorded_at"
              txPrefix="mortalityForm"
              colorScheme={colorScheme}
              formControlProps={{ isRequired: true }}
            />
            <FormControl
              labelTx="mortalityForm.basedOn"
              colorScheme={colorScheme}
              labelColor={colorScheme === "light" ? color.palette.gray800 : color.palette.gray200}
              style={FORM_CONTROL}
            >
              <Radio.Group
                colorScheme={colorScheme}
                name="basedOn"
                accessibilityLabel={translate("mortalityForm.basedOn")}
                value={basedOn}
                onChange={onChangeBasedOn}
              >
                <Radio value="quantity" mr="32px" mb="8px">
                  <Text tx="mortalityForm.basedOnQuantity" preset="body2" ml="6px" colorScheme={colorScheme} />
                </Radio>
                <Radio value="size">
                  <Text tx="mortalityForm.basedOnSize" preset="body2" ml="6px" colorScheme={colorScheme} />
                </Radio>
              </Radio.Group>
            </FormControl>
            <NumeralField
              FieldComponent={Field}
              name="total_weight"
              txPrefix="mortalityForm"
              colorScheme={colorScheme}
              input={input}
              nextInput={nextInput}
              formControlProps={{ isRequired: true }}
              numeralInputProps={{
                format: "0,0.[00]",
                onChangeNumber: async (value) => {
                  await setFieldValue("total_weight", value)
                  await setFieldTouched("total_weight")
                  if (value != null && values.size != null)
                    await setFieldValue("quantity", value * values.size)
                  else await setFieldValue("quantity", null)
                }
              }}
            />
            {basedOn === "quantity" ? <NumeralField
              FieldComponent={Field}
              name="quantity"
              txPrefix="mortalityForm"
              colorScheme={colorScheme}
              input={input}
              nextInput={nextInput}
              formControlProps={{ isRequired: true }}
              numeralInputProps={{
                format: "0,0",
                onChangeNumber: async (value) => {
                  await setFieldValue("quantity", value)
                  await setFieldTouched("quantity")
                  if (value != null && values.total_weight != null) {
                    await setFieldValue("average_weight", (1000 * values.total_weight / value) || null)
                    await setFieldValue("size", value / values.total_weight)
                  }
                  else {
                    await setFieldValue("average_weight", 0)
                    await setFieldValue("size", 0)
                  }
                }
              }}
            /> : <NumeralField
              FieldComponent={Field}
              name="size"
              txPrefix="mortalityForm"
              colorScheme={colorScheme}
              input={input}
              nextInput={nextInput}
              formControlProps={{ isRequired: true }}
              numeralInputProps={{
                format: "0,0",
                onChangeNumber: async (value) => {
                  await setFieldValue("size", value)
                  await setFieldTouched("size")
                  await setFieldValue("average_weight", (1000 / value) || null)
                  if (value != null && values.total_weight != null)
                    await setFieldValue("quantity", value * values.total_weight)
                  else await setFieldValue("quantity", null)
                }
              }}
            />}
            <NumeralField
              FieldComponent={Field}
              name="multiplier"
              txPrefix="mortalityForm"
              colorScheme={colorScheme}
              input={input}
              nextInput={nextInput}
              formControlProps={{
                labelComponent: <View>
                  <Text colorScheme={colorScheme} preset="title4" tx="mortalityForm.multiplier" />
                  <Text preset="caption2" color={color.palette.gray600} tx="mortalityForm.multiplierInfo" />
                </View>
              }}
              numeralInputProps={{
                format: "0,0.[00]",
                onChangeNumber: async (value) => {
                  await setFieldValue("multiplier", (value || 1))
                  await setFieldTouched("multiplier")
                }
              }}
            />
            <TextField
              key={`quantityFinal-${values.multiplier}-${values.quantity}`}
              name="quantityFinal"
              txPrefix="mortalityForm"
              colorScheme={colorScheme}
              input={input}
              nextInput={nextInput}
              formControlProps={{
                inputProps: { isReadOnly: true },
                children: <Text text={numeral(values.quantity * (values.multiplier || 1)).format('0,0')} preset="title4" colorScheme={colorScheme} />
              }}
            />
            <MultipleImageField
              name="files"
              txPrefix="mortalityForm"
              colorScheme={colorScheme}
            />
            <TextField
              name="remark"
              txPrefix="mortalityForm"
              colorScheme={colorScheme}
              input={input}
              nextInput={nextInput}
              formControlProps={{ inputProps: { multiline: true, numberOfLines: 3, onSubmitEditing: undefined } }}
            />
          </WalkthroughElement>
        </>
      )}
    </Formik>
  )
})

MortalityForm.displayName = 'MortalityForm'
